import { FC, Fragment, useState } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { X, Check, Copy } from 'lucide-react';
import { toast } from 'react-toastify';
import { SubmitButton, SecondaryButton } from '@/components/ui/Button';

interface AIResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  options: string[];
  onSave: (selectedOption: string) => void;
  onAfterSave?: () => void | Promise<void>;
  title: string;
  description?: string;
  saveButtonLabel?: string;
  isLoading?: boolean;
}

const AIResultModal: FC<AIResultModalProps> = ({
  isOpen,
  onClose,
  options,
  onSave,
  onAfterSave,
  title,
  description,
  saveButtonLabel = 'Save to Profile',
  isLoading = false,
}) => {
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  const handleSave = async () => {
    if (selectedOption !== null && options[selectedOption]) {
      onSave(options[selectedOption]);
      if (onAfterSave) {
        await onAfterSave();
      }
    }
  };

  const handleClose = () => {
    setSelectedOption(null);
    onClose();
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-2"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-2"
          >
            <DialogPanel className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <DialogTitle className="text-xl font-semibold text-gray-900">{title}</DialogTitle>
                  {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
                </div>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isLoading}
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Options */}
              <div className="p-6">
                {options.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No options generated</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {options.map((option, index) => (
                      <div
                        key={index}
                        onClick={() => !isLoading && setSelectedOption(index)}
                        className={`relative border-2 rounded-lg transition-all cursor-pointer ${
                          selectedOption === index
                            ? 'border-blue-500 bg-blue-50 shadow-lg'
                            : 'border-gray-200 hover:border-blue-300 bg-white hover:shadow-md'
                        } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {/* Selected indicator badge */}
                        {selectedOption === index && (
                          <div className="absolute -top-2 -right-2 bg-blue-500 text-white rounded-full p-1.5 shadow-md z-10">
                            <Check className="w-4 h-4" />
                          </div>
                        )}

                        {/* Card header with copy button */}
                        <div className="flex items-center justify-between px-5 pt-4 pb-2">
                          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                            Option {index + 1}
                          </div>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(option);
                              toast.success('Copied to clipboard!');
                            }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Copy to clipboard"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Option content */}
                        <div className="px-5 pb-5">
                          <p className="text-sm text-gray-900 leading-relaxed">{option}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                <SecondaryButton onClick={handleClose} disabled={isLoading}>
                  Cancel
                </SecondaryButton>
                <SubmitButton
                  type="button"
                  onClick={handleSave}
                  loading={isLoading}
                  disabled={isLoading || selectedOption === null}
                >
                  {isLoading ? 'Saving...' : saveButtonLabel}
                </SubmitButton>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AIResultModal;
